/**
 * THY AppFeedback bileşeninin sayfaya alınması (096 / FR-006, FR-011, K-05).
 *
 * Dosyalar `public/` altında statik varlık olarak durur ve **çalışma anında** enjekte edilir.
 * `index.html`'e statik etiket koymak yerine bu yol seçildi çünkü THY dışı ortamda dosyaların
 * **hiç indirilmemesi** gerekiyor (FR-006) — betiğin varlığı tek başına istek üretmese de,
 * indirilmesi gereksiz ve gözlemlenebilir bir yan etkidir.
 *
 * Enjeksiyon **bir kez** yapılır: sonuç önbelleğe alınır, sonraki tetiklemeler aynı sözü paylaşır.
 */

import type { AppFeedbackWidget } from './widgetTypes'

/**
 * Satıcı dosyaları — THY'den geldiği gibi, sürüm adlarıyla. Sürüm değişince bu yollar güncellenir.
 * Künye ve yeni sürüm yordamı: `VENDOR.md` (bu dizinde).
 */
const VENDOR_CSS = '/vendor/appfeedback/app_v2.css'
const VENDOR_JS = '/vendor/appfeedback/app_v4.js'

/** Bizim örtü stilimiz. Satıcı CSS'inden SONRA yüklenmek zorunda (bkz. dosyanın kendi başlığı). */
const OVERRIDE_CSS = '/appfeedback-overrides.css'

/** Bileşen kendini bu elementin `innerHTML`'ine yazar. */
const ROOT_ID = 'tk-app-feedback'

let pending: Promise<AppFeedbackWidget | null> | null = null

/**
 * Kopya-önleme yalnız **başarıyla yüklenmiş** etiketleri sayar.
 *
 * Varlığa değil bu işarete bakmak zorunludur: başarısız bir etiket de DOM'da kalır ve "zaten var"
 * kontrolü onu yüklenmiş sanıp anında `resolve` ederdi. Sonuç, yeniden denemenin **hiç
 * çalışmaması** olurdu — `ensureAppFeedbackWidget` önbelleği sıfırlasa bile her sonraki deneme
 * ölü etikete takılıp anında dönerdi. (GitLab Duo code review bulgusu, MR !184.)
 */
const LOADED_FLAG = 'appfeedbackLoaded'

function injectStylesheet(href: string): Promise<void> {
  return new Promise((resolve) => {
    if (document.querySelector(`link[href="${href}"][data-appfeedback-loaded="true"]`)) {
      resolve()
      return
    }
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    // Stil yüklenemezse anket biçimsiz görünür ama ÇALIŞIR — bu yüzden hata da `resolve` eder.
    // Yine de etiket kaldırılır: aksi hâlde geçici bir hata, anketi o oturum boyunca kalıcı
    // olarak biçimsiz bırakırdı (betikteki hatanın daha zararsız kardeşi).
    link.addEventListener('load', () => {
      link.dataset[LOADED_FLAG] = 'true'
      resolve()
    })
    link.addEventListener('error', () => {
      link.remove()
      resolve()
    })
    document.head.appendChild(link)
  })
}

function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"][data-appfeedback-loaded="true"]`)) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.addEventListener('load', () => {
      script.dataset[LOADED_FLAG] = 'true'
      resolve()
    })
    script.addEventListener('error', () => {
      // Ölü etiket DOM'da bırakılırsa sonraki denemeler ona takılır ve betik bir daha ASLA
      // yeniden istenmez — yeniden deneme mekanizması sessizce ölür.
      script.remove()
      reject(new Error(`AppFeedback betiği yüklenemedi: ${src}`))
    })
    document.head.appendChild(script)
  })
}

/**
 * Bileşenin yazacağı kök elementi hazırlar.
 *
 * **React kökünün DIŞINDA** olmalıdır: React kendi ağacını yeniden render ettiğinde içine yazılmış
 * yabancı DOM'u silebilir. `document.body`'ye doğrudan eklenmesi bunu garanti eder.
 */
function ensureRootElement(): void {
  if (document.getElementById(ROOT_ID)) return
  const root = document.createElement('div')
  root.id = ROOT_ID
  document.body.appendChild(root)
}

async function load(): Promise<AppFeedbackWidget | null> {
  ensureRootElement()
  // Sıra önemli: önce satıcı stili, SONRA bizim örtümüz (ikisi de `!important` kullanıyor,
  // eşit özgüllükte sonra gelen kazanır).
  await injectStylesheet(VENDOR_CSS)
  await injectStylesheet(OVERRIDE_CSS)
  await injectScript(VENDOR_JS)
  return window.appFeedback ?? null
}

/**
 * Bileşeni (gerekiyorsa) yükler ve global nesnesini döndürür.
 *
 * @returns bileşen hazırsa global nesne, betik yüklenip de nesneyi tanımlamadıysa `null`
 */
export function ensureAppFeedbackWidget(): Promise<AppFeedbackWidget | null> {
  pending ??= load().catch((error: unknown) => {
    // Bir sonraki tetiklemede yeniden denenebilsin diye önbellek sıfırlanır; hata çağırana
    // fırlatılır, `trigger.ts` orada yutar ve teşhis izine yazar.
    pending = null
    throw error
  })
  return pending
}

/**
 * Ekranda açık bir anket varsa kapatır. Oturum sonu ve vekâlet geçişinde çağrılır (FR-010, FR-014).
 *
 * Dosyalar yüklü kalır — yeniden yüklemenin anlamı yok; kapatılan yalnız render edilmiş ankettir.
 * Her adım korumalı: temizlik akışında (çıkış, persona geçişi) atılan bir hata kullanıcıyı yarı
 * kapanmış bir oturumda bırakırdı.
 */
export function dismissAppFeedbackWidget(): void {
  try {
    window.appFeedback?.destroy?.()
  } catch {
    /* bileşen kaldırılmış veya sürüm değişmiş olabilir — temizlik best-effort */
  }
}

/** Ekranda şu an açık bir anket var mı? */
export function isAppFeedbackVisible(): boolean {
  const root = document.getElementById(ROOT_ID)
  return root !== null && root.childElementCount > 0
}
